import { AggregationsContainer } from './AggregationsContainer';

export type TAggregatedFields =
  | 'RTT'
  | 'AudioJitter'
  | 'VideoJitter'
  | 'Buffer'
  | 'TargetBuffer'
  | 'ChunkLoadDuration'
  | 'ChunkLoadTTFB'
  | 'PlaylistLoadDuration'
  | 'FramesEncoded'
  | 'FramesSent'
  | 'ChunkSize'
  | 'PlaylistSize'
  | 'PlaylistLoadTTFB';

type TCalculatedValueFields =
  | `max${TAggregatedFields}`
  | `median${TAggregatedFields}`
  | `sum${TAggregatedFields}`
  | `count${TAggregatedFields}`;

export class AggregatedMetrics {
  private _aggregatedFields: Record<TAggregatedFields, AggregationsContainer> = {
    RTT: new AggregationsContainer(['max', 'median']),
    AudioJitter: new AggregationsContainer(['max', 'median']),
    VideoJitter: new AggregationsContainer(['max', 'median']),
    Buffer: new AggregationsContainer(['max', 'median']),
    TargetBuffer: new AggregationsContainer(['max', 'median']),
    FramesEncoded: new AggregationsContainer(['max', 'median']),
    FramesSent: new AggregationsContainer(['max', 'median']),
    ChunkLoadDuration: new AggregationsContainer(['max', 'median', 'sum']),
    ChunkLoadTTFB: new AggregationsContainer(['max', 'median', 'sum']),
    PlaylistLoadDuration: new AggregationsContainer(['max', 'median', 'sum']),
    PlaylistLoadTTFB: new AggregationsContainer(['max', 'median', 'sum']),
    PlaylistSize: new AggregationsContainer(['sum', 'count']),
    ChunkSize: new AggregationsContainer(['sum', 'count']),
  };

  public addValue(field: TAggregatedFields, value: number): void {
    this._aggregatedFields[field]?.addValue(value);
  }

  public addValues(field: TAggregatedFields, values: number[]): void {
    this._aggregatedFields[field]?.addValues(values);
  }

  public clearValues(field: TAggregatedFields): void {
    this._aggregatedFields[field]?.clear();
  }

  public clearAllValues(): void {
    Object.values(this._aggregatedFields).forEach((field) => field.clear());
  }

  public getMax(field: TAggregatedFields): void {
    this._aggregatedFields[field].max;
  }

  public getMedian(field: TAggregatedFields): void {
    this._aggregatedFields[field].median;
  }

  public getAllValues(): Partial<Record<TCalculatedValueFields, number>> {
    const result: Partial<Record<TCalculatedValueFields, number>> = {};

    Object.keys(this._aggregatedFields).forEach((key: TAggregatedFields) => {
      const max: number | null = this._aggregatedFields[key].max;

      if (max !== null && max > -Infinity) {
        result[`max${key}`] = max;
      }

      const median: number | null = this._aggregatedFields[key].median;

      if (median !== null) {
        result[`median${key}`] = median;
      }

      const sum = this._aggregatedFields[key].sum;

      if (sum) {
        result[`sum${key}`] = sum;
      }

      const count = this._aggregatedFields[key].count;

      if (count) {
        result[`count${key}`] = count;
      }
    });

    return result;
  }
}
